<script lang="ts">
  import { 
    X, AlertTriangle, Droplets, CheckCircle2, ArrowRight, ArrowLeft,
    Wrench, Sparkles, Check, Clock, ShieldCheck, Flame, HelpCircle,
    Play, Pause, RotateCcw, Activity
  } from 'lucide-svelte';
  import { onMount, onDestroy } from 'svelte';
  import { unitPreference, formatFlowRate } from '../stores/preferences';
  import { telemetry } from '../stores/telemetry';

  interface DiagnosticSession {
    diagnostic_id: string;
    anomaly_type: string;
    flow_rate_lph: number;
    status: string;
    suspected_fixture: string;
    test_completed?: string;
    resolution_notes?: string;
    work_order_id?: string;
  }

  let { 
    open = $bindable(false),
    propertyId = 700,
    unitId = '',
    unitLabel = 'Your Unit',
    onResolved = () => {}
  } = $props<{
    open: boolean;
    propertyId?: number;
    unitId?: string;
    unitLabel?: string;
    onResolved?: () => void;
  }>();

  let session = $state<DiagnosticSession | null>(null);
  let step = $state<number>(1);
  let selectedFixture = $state<string>('toilet');
  let dyeTestPassed = $state<boolean | null>(null);
  let isolationTestPassed = $state<boolean | null>(null);
  let residentNotes = $state<string>('');
  let loading = $state(false);
  let dispatching = $state(false);
  let feedback = $state('');

  // Live telemetry flow & diagnostic timer
  let liveFlowLPH = $state(42);
  let timerDuration = $state(600);
  let timerSecondsLeft = $state(600);
  let timerActive = $state(false);
  let timerInterval: any = null;
  let unsubTelemetry: (() => void) | null = null;

  function startTimer(durationSec: number) {
    if (timerInterval) clearInterval(timerInterval);
    timerDuration = durationSec;
    timerSecondsLeft = durationSec;
    timerActive = true;
    timerInterval = setInterval(() => {
      if (timerSecondsLeft > 0) {
        timerSecondsLeft--;
      } else {
        clearInterval(timerInterval);
        timerActive = false;
      }
    }, 1000);
  }

  function toggleTimer() {
    if (timerActive) {
      if (timerInterval) clearInterval(timerInterval);
      timerActive = false;
    } else {
      if (timerSecondsLeft <= 0) {
        timerSecondsLeft = timerDuration;
      }
      timerActive = true;
      timerInterval = setInterval(() => {
        if (timerSecondsLeft > 0) {
          timerSecondsLeft--;
        } else {
          clearInterval(timerInterval);
          timerActive = false;
        }
      }, 1000);
    }
  }

  function resetTimer(defaultSec?: number) {
    if (timerInterval) clearInterval(timerInterval);
    timerActive = false;
    if (defaultSec !== undefined) timerDuration = defaultSec;
    timerSecondsLeft = timerDuration;
  }

  function formatTimer(sec: number): string {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  }

  onMount(() => {
    loadDiagnostic();
    unsubTelemetry = telemetry.subscribe(state => {
      if (state.latestEvent && state.latestEvent.flow_rate !== undefined && state.latestEvent.flow_rate !== null) {
        liveFlowLPH = state.latestEvent.flow_rate;
      }
    });
  });

  onDestroy(() => {
    if (timerInterval) clearInterval(timerInterval);
    if (unsubTelemetry) unsubTelemetry();
  });

  $effect(() => {
    if (open) {
      loadDiagnostic();
      step = 1;
      feedback = '';
    }
  });

  async function loadDiagnostic() {
    loading = true;
    try {
      const res = await fetch(`/api/properties/${propertyId}/resident-diagnostics?unit_id=${encodeURIComponent(unitId)}`);
      if (res.ok) {
        session = await res.json();
        if (session?.suspected_fixture) {
          selectedFixture = session.suspected_fixture;
        }
      }
    } catch (e: any) {
      // Mock fallback
      session = {
        diagnostic_id: 'diag-demo-1',
        anomaly_type: 'toilet_flapper',
        flow_rate_lph: 42,
        status: 'active',
        suspected_fixture: 'toilet'
      };
    } finally {
      loading = false;
    }
  }

  async function handleResolveSelf() {
    if (!session) return;
    dispatching = true;
    try {
      const res = await fetch(`/api/properties/${propertyId}/resident-diagnostics/${session.diagnostic_id}/resolve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          test_completed: dyeTestPassed ? 'dye_test_flapper_replaced' : 'angle_stop_isolation',
          notes: residentNotes || 'Resident confirmed fixture leak isolated and fixed.'
        })
      });
      if (!res.ok) throw new Error('Failed to resolve diagnostic');
      feedback = 'Leak diagnostic resolved and marked safe!';
      setTimeout(() => {
        open = false;
        onResolved();
      }, 1500);
    } catch (e: any) {
      feedback = e.message;
    } finally {
      dispatching = false;
    }
  }

  async function handleEscalatePlumber() {
    if (!session) return;
    dispatching = true;
    try {
      const flowRateDesc = liveFlowLPH > 0 ? `${liveFlowLPH.toFixed(1)} L/h` : '~42 L/h';
      const diagNotes = `Resident completed self-diagnosis on ${selectedFixture}. Continuous flow of ${flowRateDesc} persists. Dye test: ${dyeTestPassed ? 'leak confirmed' : 'negative'}. ${residentNotes}`;

      const res = await fetch(`/api/properties/${propertyId}/resident-diagnostics/${session.diagnostic_id}/escalate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          unit_id: unitId,
          unit_label: unitLabel,
          fixture: selectedFixture,
          flow_rate_lph: liveFlowLPH || 42,
          dye_test_passed: dyeTestPassed,
          description: diagNotes
        })
      });
      if (!res.ok) throw new Error('Failed to dispatch plumbing work order');

      // Also ensure PMS Work Order is synced directly to maintenance queue
      try {
        await fetch(`/api/properties/${propertyId}/maintenance-work-orders`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            unit_id: unitId,
            location_label: `Unit ${unitLabel}`,
            source_type: selectedFixture === 'toilet' ? 'flapper_leak' : 'fixture_leak',
            pms_target: 'appfolio',
            priority: 'urgent',
            category: 'plumbing_fixture',
            fixture_diagnosis: `In-Suite AI Diagnostic: ${selectedFixture} leak detected. Flow: ${flowRateDesc}. ${residentNotes}`,
            loss_rate_gph: liveFlowLPH > 0 ? Number((liveFlowLPH * 0.264172).toFixed(1)) : 11.1
          })
        });
      } catch (err) {
        console.warn('PMS direct sync notice:', err);
      }

      feedback = 'Plumbing Work Order created & dispatched to building maintenance (PMS Sync)!';
      setTimeout(() => {
        open = false;
        onResolved();
      }, 2000);
    } catch (e: any) {
      feedback = e.message;
    } finally {
      dispatching = false;
    }
  }
</script>

{#if open}
  <div class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
    <div class="bg-base-100 border border-base-200 rounded-3xl shadow-2xl max-w-xl w-full overflow-hidden flex flex-col">
      
      <!-- Modal Header -->
      <div class="px-6 py-4 border-b border-base-200 bg-base-200/50 flex items-center justify-between">
        <div class="flex items-center gap-3">
          <div class="w-10 h-10 rounded-2xl bg-warning/10 text-warning flex items-center justify-center font-bold">
            <Sparkles class="w-5 h-5 text-warning" />
          </div>
          <div>
            <h3 class="font-extrabold text-base-content text-base leading-tight flex items-center gap-2">
              In-Suite AI Plumbing Diagnostic
              <span class="badge badge-xs badge-warning font-bold uppercase">Step {step} of 3</span>
            </h3>
            <p class="text-xs text-base-content/60">Automated leak localization for Suite {unitLabel}</p>
          </div>
        </div>

        <button 
          class="btn btn-sm btn-ghost btn-circle text-base-content/50 hover:text-base-content"
          onclick={() => open = false}
        >
          <X class="w-4 h-4" />
        </button>
      </div>

      <!-- Feedback Banner -->
      {#if feedback}
        <div class="mx-6 mt-4 p-3 rounded-2xl bg-success/10 border border-success/20 text-success text-xs font-bold flex items-center gap-2">
          <CheckCircle2 class="w-4 h-4" />
          <span>{feedback}</span>
        </div>
      {/if}

      <!-- Modal Body -->
      <div class="p-6 space-y-6">
        
        <!-- STEP 1: Anomaly Overview & Fixture Selection -->
        {#if step === 1}
          <div class="space-y-4 animate-fade-in">
            <!-- Warning Banner -->
            <div class="p-4 rounded-2xl bg-warning/10 border border-warning/20 flex items-start gap-3 text-warning-content">
              <AlertTriangle class="w-5 h-5 text-warning shrink-0 mt-0.5" />
              <div class="text-xs space-y-1">
                <span class="font-black text-sm block text-base-content">Continuous Micro-Flow Detected</span>
                <p class="text-base-content/70">
                  Your smart submeter detected an uninterrupted flow of <strong>~42 L/h (0.18 GPM)</strong> for over 90 minutes. Left unattended, this can waste ~300 gallons/day (~$38/month).
                </p>
              </div>
            </div>

            <!-- Fixture Selection Grid -->
            <div>
              <span class="block py-1 text-xs font-bold text-base-content/70">
                Suspected Plumbing Fixture (Most common: Toilet Flapper)
              </span>
              <div class="grid grid-cols-2 gap-2 text-xs">
                <button 
                  class="p-3.5 rounded-2xl border text-left font-bold transition-all flex items-center gap-2.5 {selectedFixture === 'toilet' ? 'border-primary bg-primary/10 text-primary shadow-xs' : 'border-base-200 bg-base-100 hover:bg-base-200'}"
                  onclick={() => selectedFixture = 'toilet'}
                >
                  <Droplets class="w-4 h-4" />
                  <div>
                    <span>Toilet Flapper / Fill Valve</span>
                    <span class="text-[10px] block font-normal text-base-content/50">84% of apartment leaks</span>
                  </div>
                </button>

                <button 
                  class="p-3.5 rounded-2xl border text-left font-bold transition-all flex items-center gap-2.5 {selectedFixture === 'faucet' ? 'border-primary bg-primary/10 text-primary shadow-xs' : 'border-base-200 bg-base-100 hover:bg-base-200'}"
                  onclick={() => selectedFixture = 'faucet'}
                >
                  <Wrench class="w-4 h-4" />
                  <div>
                    <span>Vanity or Sink Faucet</span>
                    <span class="text-[10px] block font-normal text-base-content/50">Dripping aerator / cartridge</span>
                  </div>
                </button>

                <button 
                  class="p-3.5 rounded-2xl border text-left font-bold transition-all flex items-center gap-2.5 {selectedFixture === 'shower' ? 'border-primary bg-primary/10 text-primary shadow-xs' : 'border-base-200 bg-base-100 hover:bg-base-200'}"
                  onclick={() => selectedFixture = 'shower'}
                >
                  <Droplets class="w-4 h-4" />
                  <div>
                    <span>Shower Head / Diverter</span>
                    <span class="text-[10px] block font-normal text-base-content/50">Internal cartridge bypass</span>
                  </div>
                </button>

                <button 
                  class="p-3.5 rounded-2xl border text-left font-bold transition-all flex items-center gap-2.5 {selectedFixture === 'appliances' ? 'border-primary bg-primary/10 text-primary shadow-xs' : 'border-base-200 bg-base-100 hover:bg-base-200'}"
                  onclick={() => selectedFixture = 'appliances'}
                >
                  <HelpCircle class="w-4 h-4" />
                  <div>
                    <span>Ice Maker / RO Filter</span>
                    <span class="text-[10px] block font-normal text-base-content/50">Continuous membrane purge</span>
                  </div>
                </button>
              </div>
            </div>
          </div>

        <!-- STEP 2: Guided Test Instructions & Live Diagnostic Timer -->
        {:else if step === 2}
          <div class="space-y-4 animate-fade-in text-xs">
            <!-- Live Submeter Flow Monitor -->
            <div class="p-3.5 rounded-2xl bg-base-200 border border-base-300 flex items-center justify-between">
              <div class="flex items-center gap-2.5">
                <div class="w-8 h-8 rounded-xl flex items-center justify-center {liveFlowLPH > 0 ? 'bg-warning/20 text-warning animate-pulse' : 'bg-success/20 text-success'}">
                  <Activity class="w-4 h-4" />
                </div>
                <div>
                  <span class="text-[10px] uppercase font-bold text-base-content/60 block">Live Unit Submeter Flow</span>
                  <span class="font-extrabold text-sm text-base-content font-mono">
                    {formatFlowRate(liveFlowLPH, $unitPreference)}
                  </span>
                </div>
              </div>
              <span class="badge badge-sm font-bold {liveFlowLPH > 0 ? 'badge-warning' : 'badge-success text-white'}">
                {liveFlowLPH > 0 ? 'Flow Active' : 'Zero Flow (Safe)'}
              </span>
            </div>

            <!-- Interactive Diagnostic Timer -->
            <div class="p-3.5 rounded-2xl bg-primary/5 border border-primary/20 space-y-2">
              <div class="flex items-center justify-between">
                <div class="flex items-center gap-1.5 font-bold text-xs text-primary">
                  <Clock class="w-3.5 h-3.5" />
                  <span>{selectedFixture === 'toilet' ? '10-Minute Observation Timer' : '2-Minute Isolation Timer'}</span>
                </div>
                <div class="flex items-center gap-1">
                  <button 
                    class="btn btn-xs btn-outline btn-primary rounded-lg text-[10px]"
                    onclick={() => resetTimer(selectedFixture === 'toilet' ? 600 : 120)}
                  >
                    <RotateCcw class="w-2.5 h-2.5" /> Reset
                  </button>
                  <button 
                    class="btn btn-xs {timerActive ? 'btn-warning' : 'btn-primary text-white'} rounded-lg text-[10px] gap-1"
                    onclick={toggleTimer}
                  >
                    {#if timerActive}
                      <Pause class="w-2.5 h-2.5" /> Pause
                    {:else}
                      <Play class="w-2.5 h-2.5" /> {timerSecondsLeft === timerDuration ? 'Start' : 'Resume'}
                    {/if}
                  </button>
                </div>
              </div>

              <div class="flex items-center justify-between pt-1">
                <span class="text-2xl font-black font-mono tracking-wider text-base-content">
                  {formatTimer(timerSecondsLeft)}
                </span>
                <span class="text-[11px] text-base-content/60">
                  {#if timerSecondsLeft === 0}
                    <span class="text-success font-bold">Timer Complete! Observe results now.</span>
                  {:else if timerActive}
                    <span class="text-primary animate-pulse font-medium">Timer counting down...</span>
                  {:else}
                    <span>Ready when you are</span>
                  {/if}
                </span>
              </div>
            </div>

            {#if selectedFixture === 'toilet'}
              <!-- Toilet Flapper Dye Test Card -->
              <div class="p-4 rounded-2xl bg-base-200 border border-base-300 space-y-3">
                <div class="flex items-center gap-2 font-bold text-sm text-primary">
                  <Sparkles class="w-4 h-4" />
                  <span>The 10-Minute Food Coloring Dye Test</span>
                </div>
                <ol class="list-decimal pl-4 space-y-2 text-base-content/80 leading-relaxed">
                  <li>Remove the toilet tank lid carefully and place it on a towel.</li>
                  <li>Add <strong>4 to 5 drops of food coloring</strong> (blue, green, or red) or dark liquid directly into the tank water.</li>
                  <li><strong>Do not flush the toilet</strong> for 10 to 15 minutes.</li>
                  <li>Inspect the toilet bowl. If colored water appears in the bowl without flushing, your rubber flapper seal is deteriorated and leaking!</li>
                </ol>
              </div>

              <!-- Test Result Selection -->
              <div class="space-y-1.5">
                <span class="font-bold text-base-content/70">What did you observe?</span>
                <div class="grid grid-cols-2 gap-2">
                  <button 
                    class="p-3 rounded-xl border text-left font-bold flex items-center gap-2 {dyeTestPassed === true ? 'border-error bg-error/10 text-error' : 'border-base-200 hover:bg-base-200'}"
                    onclick={() => dyeTestPassed = true}
                  >
                    <span>Bowl turned colored</span>
                    <span class="badge badge-xs badge-error text-white font-bold">Leaking Flapper</span>
                  </button>

                  <button 
                    class="p-3 rounded-xl border text-left font-bold flex items-center gap-2 {dyeTestPassed === false ? 'border-success bg-success/10 text-success' : 'border-base-200 hover:bg-base-200'}"
                    onclick={() => dyeTestPassed = false}
                  >
                    <span>Bowl stayed clear</span>
                    <span class="badge badge-xs badge-success text-white font-bold">Toilet OK</span>
                  </button>
                </div>
              </div>
            {:else}
              <!-- Angle Stop Isolation Test -->
              <div class="p-4 rounded-2xl bg-base-200 border border-base-300 space-y-3">
                <div class="flex items-center gap-2 font-bold text-sm text-primary">
                  <Wrench class="w-4 h-4" />
                  <span>Angle Stop Valve Isolation Test</span>
                </div>
                <ol class="list-decimal pl-4 space-y-2 text-base-content/80 leading-relaxed">
                  <li>Locate the oval valve knob under the fixture supply lines.</li>
                  <li>Turn the valve handle <strong>clockwise until hand-tight</strong> to shut off water to this specific fixture.</li>
                  <li>Wait 2 minutes while observing your smart meter flow reading above.</li>
                  <li>If the flow drops to <strong>0.0 GPM</strong>, this fixture is confirmed as the source of the leak!</li>
                </ol>
              </div>

              <div class="space-y-1.5">
                <span class="font-bold text-base-content/70">Isolation Result:</span>
                <div class="grid grid-cols-2 gap-2">
                  <button 
                    class="p-3 rounded-xl border text-left font-bold {isolationTestPassed === true ? 'border-primary bg-primary/10 text-primary' : 'border-base-200'}"
                    onclick={() => isolationTestPassed = true}
                  >
                    Flow stopped when valve turned off
                  </button>
                  <button 
                    class="p-3 rounded-xl border text-left font-bold {isolationTestPassed === false ? 'border-base-300 bg-base-200' : 'border-base-200'}"
                    onclick={() => isolationTestPassed = false}
                  >
                    Flow continued from another line
                  </button>
                </div>
              </div>
            {/if}
          </div>

        <!-- STEP 3: Resolution & Action -->
        {:else if step === 3}
          <div class="space-y-4 animate-fade-in text-xs">
            <div class="p-4 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-800 space-y-1">
              <span class="font-extrabold text-sm block">Diagnostic Finding Ready</span>
              <p>
                Suspected cause: <strong>{selectedFixture === 'toilet' ? 'Toilet Tank Flapper / Fill Seal' : selectedFixture}</strong>.
              </p>
            </div>

            <!-- Notes Field -->
            <div class="form-control">
              <label class="label py-0.5 font-bold text-base-content/70" for="resident-notes">Resident Diagnostic Notes</label>
              <textarea 
                id="resident-notes"
                bind:value={residentNotes} 
                class="textarea textarea-bordered rounded-2xl text-xs h-20"
                placeholder="e.g., Jiggled handle and flapper seated, or bowl dye test positive."
              ></textarea>
            </div>

            <!-- Two Resolution Pathways -->
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
              <!-- Option A: Self Resolved -->
              <button 
                onclick={handleResolveSelf}
                disabled={dispatching}
                class="p-4 rounded-2xl border border-success/30 bg-success/10 hover:bg-success/20 text-left transition-all group"
              >
                <div class="flex items-center gap-2 text-success font-black text-sm">
                  <CheckCircle2 class="w-4 h-4" />
                  <span>I Fixed the Leak</span>
                </div>
                <p class="text-[11px] text-base-content/70 mt-1">
                  Handle was stuck or flapper reseated. Log resolution and clear active telemetry alarm.
                </p>
              </button>

              <!-- Option B: Dispatch Plumber Work Order -->
              <button 
                onclick={handleEscalatePlumber}
                disabled={dispatching}
                class="p-4 rounded-2xl border border-primary/30 bg-primary/10 hover:bg-primary/20 text-left transition-all group"
              >
                <div class="flex items-center gap-2 text-primary font-black text-sm">
                  <Wrench class="w-4 h-4" />
                  <span>Request Plumber</span>
                </div>
                <p class="text-[11px] text-base-content/70 mt-1">
                  1-click dispatch to property management maintenance team with diagnostic pre-brief.
                </p>
              </button>
            </div>
          </div>
        {/if}

      </div>

      <!-- Modal Footer -->
      <div class="px-6 py-4 border-t border-base-200 bg-base-200/50 flex items-center justify-between">
        {#if step > 1}
          <button 
            onclick={() => step--}
            class="btn btn-sm btn-ghost rounded-xl text-xs gap-1.5"
          >
            <ArrowLeft class="w-3.5 h-3.5" /> Back
          </button>
        {:else}
          <div></div>
        {/if}

        <div class="flex items-center gap-2">
          {#if step < 3}
            <button 
              onclick={() => step++}
              class="btn btn-sm btn-primary rounded-xl text-xs font-bold text-white px-5 gap-1.5"
            >
              Continue to Step {step + 1} <ArrowRight class="w-3.5 h-3.5" />
            </button>
          {:else}
            <button 
              onclick={() => open = false}
              class="btn btn-sm btn-ghost rounded-xl text-xs"
            >
              Cancel
            </button>
          {/if}
        </div>
      </div>

    </div>
  </div>
{/if}
